import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileSpreadsheet, Plus, Search, Filter,
    Edit, Trash2, CheckCircle, XCircle, Send,
    ArrowRightCircle, Clock, Loader2, Link2, X,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import { PreventiviService, PraticaService } from '../services/apiService';
import { useToast } from "../components/ToastContext";
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import ClientBadge from '../components/ClientBadge';

// Tipi locali, allineati all'entity Preventivo restituita dal backend:
// - client (oggetto completo) oppure tempClientName se il cliente non è registrato
// - numeroProgressivo al posto di "numero"
// - createdAt al posto di "dataCreazione"
interface PreventivoDto {
    id: number;
    numeroProgressivo?: string;
    client?: {
        id?: number;
        nome?: string;
        surname?: string;
        ragioneSociale?: string;
        tipo?: string;
    } | null;
    tempClientName?: string | null;
    createdAt?: string;
    totale: number;
    stato: 'BOZZA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO';
    // Distingue i preventivi veri dalle proforma generate da un mandato (TabContabilita).
    tipoDocumento?: 'PREVENTIVO' | 'PROFORMA';
}

// Nome cliente:
// - Azienda → "Nome Cognome (Ragione Sociale)", o solo la ragione sociale se manca il referente
// - Privato → "Nome Cognome"
// - altrimenti tempClientName (contatto non in rubrica, in lista con "(non registrato)") o null
const getClienteNome = (p: PreventivoDto): string | null => {
    if (p.client) {
        const isAzienda = (p.client.tipo || '').toLowerCase() === 'azienda';
        const referente = `${p.client.nome || ''} ${p.client.surname || ''}`.trim();
        const ragSoc = (p.client.ragioneSociale || '').trim();
        if (isAzienda) {
            if (referente && ragSoc) return `${referente} (${ragSoc})`;
            if (ragSoc) return ragSoc;
            if (referente) return referente;
        } else {
            if (referente) return referente;
            if (ragSoc) return ragSoc; // Caso limite: privato salvato con la sola ragione sociale
        }
    }
    if (p.tempClientName && p.tempClientName.trim()) return p.tempClientName.trim();
    return null;
};

export const PreventiviView: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    // Il responsabile vede i preventivi della sede scelta nel selettore; il titolare tutti.
    const { user, currentSedeId } = useAuth();
    const sedeFiltro = user?.role !== UserRole.ADMIN && typeof currentSedeId === 'number'
        ? currentSedeId : undefined;

    // Stato
    const [loading, setLoading] = useState(true);
    // Spinner a pagina intera solo al primo caricamento: le ricerche successive
    // non devono smontare la pagina, altrimenti l'input perde il focus.
    const [initialLoad, setInitialLoad] = useState(true);
    const [preventivi, setPreventivi] = useState<PreventivoDto[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    // I tab si distinguono per collegamento al mandato (pratica != null):
    // STANDALONE = preventivi/proforma non collegati
    // LINKED     = preventivi/proforma collegati a un mandato
    // Il PDF (preventivo o proforma) si sceglie dal dettaglio.
    const [docTab, setDocTab] = useState<'STANDALONE' | 'LINKED'>('STANDALONE');

    // Paginazione server-side, 25 per pagina. countStandalone/countLinked sono
    // i totali per i badge dei tab, indipendenti dai filtri.
    const PAGE_SIZE = 25;
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [countStandalone, setCountStandalone] = useState(0);
    const [countLinked, setCountLinked] = useState(0);

    // Debounce della ricerca: la fetch parte solo quando l'utente smette di digitare.
    const [debouncedQuery, setDebouncedQuery] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(searchQuery), 500);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Cambio tab/filtro/ricerca → pagina 0.
    useEffect(() => {
        setCurrentPage(0);
    }, [docTab, statusFilter, debouncedQuery]);

    // "Collega a mandato": linkPrev è il preventivo selezionato, mandatiCliente
    // i mandati caricati all'apertura, linkSaving evita il doppio click.
    const [linkPrev, setLinkPrev] = useState<PreventivoDto | null>(null);
    const [mandatiCliente, setMandatiCliente] = useState<any[]>([]);
    const [loadingMandati, setLoadingMandati] = useState(false);
    const [linkSaving, setLinkSaving] = useState(false);

    const openLinkModal = async (prev: PreventivoDto) => {
        if (!prev.client?.id) {
            addToast('Cliente non registrato: non è possibile collegare a un mandato.', 'error');
            return;
        }
        setLinkPrev(prev);
        setMandatiCliente([]);
        setLoadingMandati(true);
        try {
            const res = await PraticaService.getByClient(prev.client.id);
            setMandatiCliente(res?.data || []);
        } catch (e) {
            console.error('[openLinkModal] errore caricamento mandati cliente', e);
            addToast('Errore caricamento mandati del cliente', 'error');
        } finally {
            setLoadingMandati(false);
        }
    };

    const handleLinkToPratica = async (praticaId: number | null) => {
        if (!linkPrev) return;
        setLinkSaving(true);
        try {
            await PreventiviService.linkToPratica(linkPrev.id, praticaId);
            addToast(praticaId == null ? 'Preventivo scollegato' : 'Preventivo collegato al mandato', 'success');
            setLinkPrev(null);
            // Il record cambia tab: ricarico pagina e contatori.
            loadData();
        } catch (e: any) {
            console.error('[handleLinkToPratica]', e);
            const msg = e?.response?.data?.message || 'Errore associazione al mandato';
            addToast(msg, 'error');
        } finally {
            setLinkSaving(false);
        }
    };

    // Una fetch a ogni cambio di tab/pagina/filtro/ricerca; la risposta include
    // anche i contatori per i badge.
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await PreventiviService.getPaged({
                linked: docTab === 'LINKED',
                page: currentPage,
                size: PAGE_SIZE,
                stato: statusFilter,
                q: debouncedQuery,
                sedeId: sedeFiltro,
            });
            const pageData = (res as any).data;
            if (pageData) {
                setPreventivi(pageData.content || []);
                setTotalPages(pageData.totalPages || 0);
                setTotalElements(pageData.totalElements || 0);
                setCountStandalone(pageData.countStandalone || 0);
                setCountLinked(pageData.countLinked || 0);
            } else {
                setPreventivi([]);
                setTotalPages(0);
                setTotalElements(0);
            }
        } catch (error) {
            console.error(error);
            addToast("Errore caricamento preventivi", "error");
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [docTab, currentPage, statusFilter, debouncedQuery, sedeFiltro, addToast]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Azioni

    // Cambio stato rapido dalla tabella
    const handleCambiaStato = async (id: number, nuovoStato: string) => {
        try {
            await PreventiviService.updateStato(id, nuovoStato);

            addToast("Stato preventivo aggiornato!", "success");
            loadData();
        } catch (error) {
            console.error(error);
            addToast("Errore durante l'aggiornamento dello stato", "error");
        }
    };

    const handleDelete = async (id: number) => {
        if(!window.confirm("Sei sicuro di voler eliminare questo preventivo?")) return;
        try {
            await PreventiviService.delete(id);
            addToast("Preventivo eliminato", "success");
            // Ricarica la pagina corrente, contatori compresi.
            loadData();
        } catch (e) {
            addToast("Errore eliminazione", "error");
        }
    };

    const handleConvertToPratica = (id: number) => {
        if(window.confirm("Vuoi creare una nuovo MANDATO partendo da questo preventivo?")) {
            navigate(`/pratiche/nuova?fromPreventivo=${id}`);
        }
    };

    // Filtri e separazione per tab sono gestiti da /preventivi/paged: `preventivi`
    // contiene già la pagina corrente filtrata.

    // Render helpers

    // Stato come select colorata
    const renderStatusSelect = (preventivo: PreventivoDto) => {
        // Confronto tollerante: i record storici possono avere "Inviato"/"Bozza".
        const statoNorm = (preventivo.stato || 'BOZZA').toUpperCase();
        const getStatusColor = (s: string) => {
            switch(s) {
                case 'BOZZA': return 'bg-gray-100 text-gray-700 border-gray-200';
                case 'INVIATO': return 'bg-blue-50 text-blue-700 border-blue-200';
                case 'ACCETTATO': return 'bg-green-50 text-green-700 border-green-200';
                case 'RIFIUTATO': return 'bg-red-50 text-red-700 border-red-200';
                default: return 'bg-white text-gray-700 border-gray-200';
            }
        };

        return (
            <div className="relative group/select inline-block">
                <select
                    value={statoNorm}
                    onChange={(e) => handleCambiaStato(preventivo.id, e.target.value)}
                    className={`
                        appearance-none pl-3 pr-8 py-1.5 rounded-full text-xs font-bold border outline-none cursor-pointer transition-all
                        ${getStatusColor(statoNorm)} hover:ring-2 hover:ring-offset-1 hover:ring-gray-200
                    `}
                >
                    <option value="BOZZA">BOZZA</option>
                    <option value="INVIATO">INVIATO</option>
                    <option value="ACCETTATO">ACCETTATO</option>
                    <option value="RIFIUTATO">RIFIUTATO</option>
                </select>
                {/* Icona custom per la select */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-current opacity-70">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </div>
            </div>
        );
    };

    if (initialLoad) {
        return <div className="flex h-screen items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-blue-600" size={32}/></div>;
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 pb-24">

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FileSpreadsheet className="text-blue-600" /> Gestione Preventivi e Proforma
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Crea, gestisci e invia le proposte economiche ai clienti.
                    </p>
                </div>
                <button
                    onClick={() => navigate('/preventivi/nuovo')}
                    className="bg-blue-600 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-blue-700 font-bold shadow-sm transition-all whitespace-nowrap"
                >
                    <Plus size={20}/> Nuovo Preventivo
                </button>
            </div>

            {/*
              Tab basati sul collegamento al mandato (pratica != null), non su tipoDocumento.
              Il tipo "PROFORMA" serve solo a riconoscere le proforma archiviate dal TabContabilita.
            */}
            <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200 w-full md:w-auto">
                <button
                    onClick={() => setDocTab('STANDALONE')}
                    className={`flex-1 md:flex-none px-5 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        docTab === 'STANDALONE'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Preventivi/Proforma
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${docTab === 'STANDALONE' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}>
                        {countStandalone}
                    </span>
                </button>
                <button
                    onClick={() => setDocTab('LINKED')}
                    className={`flex-1 md:flex-none px-5 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        docTab === 'LINKED'
                            ? 'bg-white text-emerald-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Associati a mandati/servizi
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${docTab === 'LINKED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                        {countLinked}
                    </span>
                </button>
            </div>

            {/* Barra strumenti e filtri */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 items-center justify-between">

                {/* Cerca */}
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Cerca per cliente o numero..."
                        className="w-full pl-10 pr-10 py-2 border bg-gray-50 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {loading && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" size={16} />
                    )}
                </div>

                {/* Filtro Stato */}
                <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                    <Filter size={18} className="text-gray-400 shrink-0"/>
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        {(['ALL', 'BOZZA', 'INVIATO', 'ACCETTATO', 'RIFIUTATO'] as const).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                                    statusFilter === status
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {status === 'ALL' ? 'Tutti' : status.charAt(0) + status.slice(1).toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tabella preventivi */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[400px]">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm min-w-[900px]">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-medium border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-4 w-32">Numero</th>
                            <th className="px-6 py-4">Cliente</th>
                            <th className="px-6 py-4 w-32">Data</th>
                            <th className="px-6 py-4 w-32 text-right">Importo</th>
                            <th className="px-6 py-4 w-40 text-center">Stato</th>
                            <th className="px-6 py-4 w-32 text-right">Azioni</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {preventivi.length > 0 ? preventivi.map((prev) => {
                            const clienteNome = getClienteNome(prev);
                            return (
                            <tr key={prev.id} className="hover:bg-gray-50 transition-colors group">
                                {/* Il numero apre il dettaglio in modifica (/preventivi/:id). */}
                                <td className="px-6 py-4 font-mono font-medium whitespace-nowrap">
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/preventivi/${prev.id}`)}
                                        title="Apri dettaglio / modifica preventivo"
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
                                    >
                                        {prev.numeroProgressivo || `ID-${prev.id}`}
                                    </button>
                                </td>
                                <td className="px-6 py-4 font-medium text-gray-900">
                                    {clienteNome
                                        ? <>{clienteNome}{!prev.client && <span className="text-gray-400 italic font-normal"> (non registrato)</span>}</>
                                        : <span className="text-gray-400 italic">Cliente non registrato</span>}
                                    <ClientBadge deleted={(prev as any).clienteDeleted} anonymized={(prev as any).clienteAnonymized} />
                                    {/*
                                      Badge del collegamento a mandato o servizio fiduciario (mai entrambi).
                                      Cliccabile, oppure "eliminato" se il record collegato è stato cancellato.
                                    */}
                                    {(prev as any).praticaId && (
                                        (prev as any).praticaDeleted ? (
                                            <span
                                                title={`Mandato #${(prev as any).praticaId} eliminato`}
                                                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded-full text-[10px] font-bold uppercase tracking-wider line-through"
                                            >
                                                Mandato #{(prev as any).praticaId} eliminato
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); navigate(`/pratiche/${(prev as any).praticaId}`); }}
                                                title={(prev as any).praticaNome || 'Apri mandato collegato'}
                                                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-100 transition-colors"
                                            >
                                                ↳ {(prev as any).praticaNome || `Mandato #${(prev as any).praticaId}`}
                                            </button>
                                        )
                                    )}
                                    {(prev as any).servizioFiduciarioId && (
                                        (prev as any).servizioFiduciarioDeleted ? (
                                            <span
                                                title={`Servizio #${(prev as any).servizioFiduciarioId} eliminato`}
                                                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded-full text-[10px] font-bold uppercase tracking-wider line-through"
                                            >
                                                Servizio #{(prev as any).servizioFiduciarioId} eliminato
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); navigate(`/servizi/${(prev as any).servizioFiduciarioId}`); }}
                                                title={(prev as any).servizioFiduciarioNome || 'Apri servizio fiduciario collegato'}
                                                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-100 transition-colors"
                                            >
                                                ↳ Servizio: {(prev as any).servizioFiduciarioNome || `#${(prev as any).servizioFiduciarioId}`}
                                            </button>
                                        )
                                    )}
                                </td>
                                <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                                    {prev.createdAt ? new Date(prev.createdAt).toLocaleDateString('it-IT') : '-'}
                                </td>
                                <td className="px-6 py-4 text-right font-bold text-gray-900 whitespace-nowrap">
                                    € {(prev.totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                                <td className="px-6 py-4 text-center">
                                    {renderStatusSelect(prev)}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">

                                        {/* Azione: Converti (solo se accettato)
                                        {(prev.stato || '').toUpperCase() === 'ACCETTATO' && (
                                            <button
                                                onClick={() => handleConvertToPratica(prev.id)}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                title="Converti in Pratica"
                                            >
                                                <ArrowRightCircle size={18}/>
                                            </button>
                                        )}*/}

                                        {/*
                                          Collega a mandato, per i preventivi creati dopo il mandato (quelli creati prima
                                          vengono collegati in PraticaService.executeCreatePraticaTransaction).
                                          Verde se già collegato; disabilitato senza cliente registrato.
                                        */}
                                        <button
                                            onClick={() => openLinkModal(prev)}
                                            disabled={!prev.client?.id}
                                            className={`p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                                (prev as any).praticaId
                                                    ? 'text-emerald-600 hover:bg-emerald-50'
                                                    : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                            title={(prev as any).praticaId ? 'Cambia / scollega mandato' : 'Collega a un mandato esistente'}
                                        >
                                            <Link2 size={18}/>
                                        </button>

                                        {/* Azione: Modifica */}
                                        <button
                                            onClick={() => navigate(`/preventivi/${prev.id}`)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Modifica"
                                        >
                                            <Edit size={18}/>
                                        </button>

                                        {/* Azione: Elimina */}
                                        <button
                                            onClick={() => handleDelete(prev.id)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Elimina"
                                        >
                                            <Trash2 size={18}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                            <Search size={24} className="text-gray-400"/>
                                        </div>
                                        <p className="font-medium">Nessun preventivo trovato</p>
                                        <p className="text-xs text-gray-400">Prova a modificare i filtri o crea un nuovo preventivo.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Footer con paginazione server-side (25 per pagina) */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-center gap-3">
                    <span>
                        {totalElements > 0 ? (
                            <>
                                Mostrati <strong>{currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalElements)}</strong> di <strong>{totalElements}</strong>
                            </>
                        ) : (
                            'Nessun risultato'
                        )}
                    </span>
                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0 || loading}
                                className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Pagina precedente"
                            >
                                <ChevronLeft size={14}/>
                            </button>
                            <span className="font-semibold text-gray-700 px-2 tabular-nums">
                                Pagina {currentPage + 1} di {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1 || loading}
                                className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Pagina successiva"
                            >
                                <ChevronRight size={14}/>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/*
              Modale "Collega a mandato": mandati attivi del cliente con evidenza di quello
              attuale. Salva con PUT /preventivi/{id}/pratica; "Scollega" imposta praticaId=null.
            */}
            {linkPrev && (
                <div
                    onClick={() => !linkSaving && setLinkPrev(null)}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
                    >
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Collega a mandato</div>
                                <div className="text-sm font-bold text-gray-900 mt-0.5">
                                    Preventivo {linkPrev.numeroProgressivo || `#${linkPrev.id}`} · {getClienteNome(linkPrev) ? `${getClienteNome(linkPrev)}${linkPrev.client ? '' : ' (non registrato)'}` : 'Cliente non registrato'}
                                </div>
                            </div>
                            <button
                                onClick={() => setLinkPrev(null)}
                                disabled={linkSaving}
                                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30"
                            >
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-4">
                            {loadingMandati ? (
                                <div className="flex items-center justify-center py-12 text-gray-400">
                                    <Loader2 className="animate-spin mr-2" size={20}/> Caricamento mandati…
                                </div>
                            ) : mandatiCliente.length === 0 ? (
                                <div className="text-center py-12 text-sm text-gray-500">
                                    Nessun mandato attivo per questo cliente.<br/>
                                    <span className="text-xs text-gray-400">Creane uno dalla sezione Mandati e tornerà qui.</span>
                                </div>
                            ) : (
                                <ul className="space-y-2">
                                    {mandatiCliente.map((m: any) => {
                                        const isLinked = (linkPrev as any).praticaId === m.id;
                                        return (
                                            <li key={m.id}>
                                                <button
                                                    onClick={() => handleLinkToPratica(m.id)}
                                                    disabled={linkSaving || isLinked}
                                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                                        isLinked
                                                            ? 'bg-emerald-50 border-emerald-200 cursor-default'
                                                            : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                                                    } disabled:opacity-60`}
                                                >
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-bold text-gray-900 truncate">{m.name || `Mandato #${m.id}`}</div>
                                                            <div className="text-[11px] text-gray-500 mt-0.5 flex gap-2 flex-wrap">
                                                                {m.stato && <span>{m.stato}</span>}
                                                                {m.createdAt && <span>· creato {new Date(m.createdAt).toLocaleDateString('it-IT')}</span>}
                                                                {m.mandatoCode && <span>· {m.mandatoCode}</span>}
                                                            </div>
                                                        </div>
                                                        {isLinked && (
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                                                                Collegato
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 rounded-b-2xl">
                            {(linkPrev as any).praticaId ? (
                                <button
                                    onClick={() => handleLinkToPratica(null)}
                                    disabled={linkSaving}
                                    className="text-xs font-bold text-red-600 hover:text-red-700 disabled:opacity-30"
                                >
                                    Scollega dal mandato corrente
                                </button>
                            ) : <span/>}
                            <button
                                onClick={() => setLinkPrev(null)}
                                disabled={linkSaving}
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1.5 disabled:opacity-30"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreventiviView;